import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarBlank, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { getServices, getDoctors, createAppointment } from "@/lib/api";

const SLOTS = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "14:00", "14:30", "15:00", "15:30", "16:00", "17:00"];

export default function BookAppointment() {
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [form, setForm] = useState({
    patient_name: "", email: "", phone: "",
    department: "", doctor_id: "none", date: null, time_slot: "", reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => { getServices().then(setServices).catch(() => {}); }, []);
  useEffect(() => {
    if (form.department) getDoctors(form.department).then(setDoctors).catch(() => setDoctors([]));
    else setDoctors([]);
  }, [form.department]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name || !form.email || !form.phone || !form.department || !form.date || !form.time_slot) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        patient_name: form.patient_name,
        email: form.email,
        phone: form.phone,
        department: form.department,
        doctor_id: form.doctor_id === "none" ? null : form.doctor_id,
        date: format(form.date, "yyyy-MM-dd"),
        time_slot: form.time_slot,
        reason: form.reason,
      };
      const res = await createAppointment(payload);
      setConfirmed(res);
      toast.success("Appointment requested! We'll confirm shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="appointment-confirmed">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E8ECE4] mb-6">
          <CheckCircle size={44} weight="fill" className="text-brand-sage" />
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Thank you, {confirmed.patient_name.split(" ")[0]}.</h1>
        <p className="mt-5 text-lg text-brand-muted">
          Your appointment request for <b className="text-brand-ink">{confirmed.department}</b> on{" "}
          <b className="text-brand-ink">{confirmed.date}</b> at <b className="text-brand-ink">{confirmed.time_slot}</b> has been received.
        </p>
        <p className="mt-3 text-brand-muted">Our team will call you at <b className="text-brand-ink">{confirmed.phone}</b> within 30 minutes to confirm.</p>
        <div className="mt-8 flex justify-center gap-3">
          <Button data-testid="book-another-btn" onClick={() => { setConfirmed(null); setForm({ patient_name: "", email: "", phone: "", department: "", doctor_id: "none", date: null, time_slot: "", reason: "" }); }} variant="outline" className="rounded-full h-12 px-7">Book another</Button>
          <Button data-testid="return-home-btn" onClick={() => nav("/")} className="rounded-full h-12 px-7 bg-brand-sage hover:bg-brand-sageDark text-white">Return home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="book-page">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <div className="text-xs uppercase tracking-[0.28em] text-brand-sage mb-4">Book an appointment</div>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.02]">A calmer visit, planned in a minute.</h1>
          <p className="mt-6 text-brand-muted text-lg leading-relaxed">
            Fill in a few details and we'll take it from there. Our team confirms every booking within 30 minutes.
          </p>
          <div className="mt-10 space-y-4 text-sm text-brand-muted">
            <div className="flex gap-3"><CheckCircle size={18} className="text-brand-sage mt-0.5" /> No account needed</div>
            <div className="flex gap-3"><CheckCircle size={18} className="text-brand-sage mt-0.5" /> Choose your doctor & time</div>
            <div className="flex gap-3"><CheckCircle size={18} className="text-brand-sage mt-0.5" /> Reschedule anytime by WhatsApp</div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-white rounded-3xl border border-brand-border p-8 md:p-10 space-y-6" data-testid="appointment-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name">Full name *</Label>
              <Input id="name" data-testid="input-name" className="mt-1.5 h-11" value={form.patient_name} onChange={(e) => update("patient_name", e.target.value)} placeholder="e.g. Ananya Rao" />
            </div>
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input id="phone" data-testid="input-phone" className="mt-1.5 h-11" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 98765 43210" />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input id="email" data-testid="input-email" type="email" className="mt-1.5 h-11" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label>Department *</Label>
              <Select value={form.department} onValueChange={(v) => { update("department", v); update("doctor_id", "none"); }}>
                <SelectTrigger data-testid="select-department" className="mt-1.5 h-11"><SelectValue placeholder="Select a department" /></SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.slug} value={s.slug} data-testid={`dept-option-${s.slug}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Preferred doctor</Label>
              <Select value={form.doctor_id} onValueChange={(v) => update("doctor_id", v)} disabled={!form.department}>
                <SelectTrigger data-testid="select-doctor" className="mt-1.5 h-11"><SelectValue placeholder={form.department ? "Any available" : "Pick department first"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any available doctor</SelectItem>
                  {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label>Preferred date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="date-picker-trigger"
                    className="mt-1.5 w-full h-11 rounded-md border border-input bg-transparent px-3 text-left text-sm flex items-center justify-between hover:border-brand-ink transition-colors"
                  >
                    {form.date ? format(form.date, "PPP") : <span className="text-muted-foreground">Pick a date</span>}
                    <CalendarBlank size={18} className="text-brand-muted" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto">
                  <Calendar
                    mode="single"
                    selected={form.date}
                    onSelect={(d) => update("date", d)}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label>Time slot *</Label>
              <Select value={form.time_slot} onValueChange={(v) => update("time_slot", v)}>
                <SelectTrigger data-testid="select-time" className="mt-1.5 h-11"><SelectValue placeholder="Pick a time" /></SelectTrigger>
                <SelectContent>
                  {SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="reason">Reason for visit (optional)</Label>
            <Textarea id="reason" data-testid="input-reason" className="mt-1.5 min-h-[100px]" value={form.reason} onChange={(e) => update("reason", e.target.value)} placeholder="Briefly describe your concern…" />
          </div>

          <Button
            type="submit"
            data-testid="submit-appointment-btn"
            disabled={submitting}
            className="w-full rounded-full h-13 py-4 bg-[#C85A40] hover:bg-[#A94A32] text-white text-base transition-transform hover:scale-[1.01]"
          >
            {submitting ? "Booking…" : "Request Appointment"}
          </Button>
          <p className="text-xs text-brand-muted text-center">By submitting, you agree to be contacted regarding your appointment.</p>
        </form>
      </div>
    </div>
  );
}
